import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchProductsByCategory,
  fetchCategories,
  clearForCategory,
  setIsFiltering,
} from '../store/productsSlice';
import Navbar from '../Components/Navbar';
import Footer from '../Components/Footer';
import ProductCard from '../Components/ProductCard';
import ProductCardSkeleton from '../Components/ProductCardSkeleton';
import LoadingSpinner from '../Components/LoadingSpinner';
import FilterSidebar from '../Components/FilterSidebar';
import VoiceSearch from '../Components/VoiceSearch';
import { Pagination } from 'antd';
import { FiSliders, FiX } from 'react-icons/fi';

const SKELETON_COUNT = 9;

// ── Chip labels from extracted Gemini filters ─────────────────────────────────
const buildChips = (extracted) => {
  if (!extracted) return [];
  const chips = [];
  if (extracted.category)       chips.push(extracted.category);
  if (extracted.brand)          chips.push(extracted.brand);
  if (extracted.color)          chips.push(extracted.color);
  if (extracted.duration)       chips.push(extracted.duration);
  if (extracted.lensesMaterial) chips.push(extracted.lensesMaterial);
  if (extracted.maxPrice && extracted.minPrice)
    chips.push(`${extracted.minPrice}–${extracted.maxPrice} EGP`);
  else if (extracted.maxPrice)  chips.push(`Under ${extracted.maxPrice} EGP`);
  else if (extracted.minPrice)  chips.push(`Over ${extracted.minPrice} EGP`);
  return chips;
};

function StoreProducts() {
  const dispatch = useDispatch();
  const { category } = useParams();
  const { state: locationState } = useLocation();
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState(
    locationState?.initialBrand?.id ? { subCategory: locationState.initialBrand.id } : {}
  );
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  // Voice search: store resolved filters to sync sidebar + show AI banner
  const [voiceFilters, setVoiceFilters]     = useState(null); // resolved ObjectId filters
  const [voiceChips, setVoiceChips]         = useState([]);   // human-readable labels

  useEffect(() => {
    if (mobileFiltersOpen) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileFiltersOpen]);

  const lastThunkRef = useRef(null);

  const {
    items: products,
    status,
    categories,
    paginationResult,
    totalResults,
    isFiltering,
    error,
  } = useSelector((state) => state.products);

  const categoryData = useMemo(
    () => categories.find((cat) => cat.slug === category),
    [categories, category]
  );
  const categoryId = categoryData?._id;

  const productsShown     = useMemo(() => products?.length || 0, [products]);
  const totalProductCount = useMemo(() => totalResults || 0, [totalResults]);

  const maxPage = useMemo(() => {
    if (paginationResult?.numberOfPages) return paginationResult.numberOfPages;
    return Math.max(1, Math.ceil(totalProductCount / 21));
  }, [paginationResult, totalProductCount]);

  const showPagination = useMemo(
    () => maxPage > 1 && totalProductCount > 0,
    [maxPage, totalProductCount]
  );

  const pageInfoText = useMemo(() => {
    if (productsShown > 0) {
      return `${totalProductCount} product${totalProductCount !== 1 ? 's' : ''}${
        showPagination ? ` · Page ${currentPage} of ${maxPage}` : ''
      }`;
    }
    return 'No products found';
  }, [totalProductCount, productsShown, currentPage, maxPage, showPagination]);

  useEffect(() => { dispatch(fetchCategories()); }, [dispatch]);

  useEffect(() => {
    if (categoryId) {
      dispatch(clearForCategory());
      setFilters(locationState?.initialBrand?.id ? { subCategory: locationState.initialBrand.id } : {});
      setCurrentPage(1);
      setVoiceFilters(null);
      setVoiceChips([]);
    }
  }, [categoryId, dispatch]); // eslint-disable-line

  useEffect(() => {
    if (currentPage > maxPage && maxPage > 0) setCurrentPage(Math.min(currentPage, maxPage));
  }, [currentPage, maxPage]);

  useEffect(() => {
    if (!categoryId) return;
    if (lastThunkRef.current) lastThunkRef.current.abort();
    lastThunkRef.current = dispatch(
      fetchProductsByCategory({ categoryId, page: currentPage, filters })
    );
  }, [dispatch, categoryId, currentPage, filters]); // eslint-disable-line

  const handleFilterChange = useCallback(
    (newFilters) => {
      if (lastThunkRef.current) lastThunkRef.current.abort();
      dispatch(setIsFiltering(true));
      setFilters(newFilters);
      setCurrentPage(1);
    },
    [dispatch]
  );

  const handlePageChange = useCallback(
    (newPage) => {
      if (newPage < 1 || newPage > maxPage) return;
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    [maxPage]
  );

  // ── Voice search: apply resolved filters into sidebar + normal fetch ─────────
  const handleVoiceResults = useCallback(
    (data) => {
      const resolved = data.filters || {};

      // Build sidebar-compatible filter object
      const newFilters = {};
      if (resolved.subCategory) newFilters.subCategory = resolved.subCategory.toString();
      if (resolved.price)        newFilters.price        = resolved.price;
      if (resolved.primaryColor)    newFilters.primaryColor    = resolved.primaryColor;
      if (resolved.duration)     newFilters.duration     = resolved.duration;
      if (resolved.lensesMaterial) newFilters.lensesMaterial = resolved.lensesMaterial;

      // Push into sidebar UI
      setVoiceFilters(resolved);
      // Show chips
      setVoiceChips(buildChips(data.extractedFilters));
      // Trigger normal product fetch (pagination, abort, etc. all work as usual)
      handleFilterChange(newFilters);
    },
    [handleFilterChange]
  );

  const clearVoiceSearch = useCallback(() => {
    setVoiceFilters(null);
    setVoiceChips([]);
    // Reset to no filters (also resets sidebar via voiceFilters=null)
    handleFilterChange({});
  }, [handleFilterChange]);

  const isLoading = status === 'loading';
  const isEmptyPageWithProducts =
    totalProductCount > 0 && productsShown === 0 && status === 'succeeded';

  if (isLoading && !isFiltering && !categoryId) {
    return (
      <div className="min-h-screen bg-cream">
        <Navbar />
        <LoadingSpinner />
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-cream">
      <Navbar />

      {/* ── Page Header ── */}
      <div className="bg-white border-b border-sand">
        <div className="container mx-auto px-4 py-4 sm:py-6">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <h1 className="font-display font-normal tracking-tight text-xl sm:text-2xl md:text-3xl text-ink capitalize">
                {categoryData?.name || category?.replace('-', ' ')}
              </h1>
              {!isLoading && totalProductCount > 0 && (
                <p className="text-clay text-sm mt-1 animate-fade-in">
                  {pageInfoText}
                </p>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <VoiceSearch onResults={handleVoiceResults} size="sm" />
              <button
                onClick={() => setMobileFiltersOpen(true)}
                className="md:hidden flex items-center gap-1.5 px-3 py-2 rounded-xl bg-cream hover:bg-brand-50 border border-sand text-ink text-sm font-medium transition-colors"
              >
                <FiSliders className="w-4 h-4" />
                Filters
              </button>
            </div>
          </div>

          {/* ── AI filter chips banner ── */}
          {voiceChips.length > 0 && (
            <div className="mt-3 flex items-center gap-2 flex-wrap animate-fade-in">
              {/* Gradient AI badge */}
              <span
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold text-ink"
                style={{ background: 'linear-gradient(135deg, #e9c478, #d4922a)' }}
              >
                <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2l1.8 5.4L19.2 9l-5.4 1.8L12 16.2l-1.8-5.4L4.8 9l5.4-1.8z" />
                </svg>
                AI
              </span>

              {voiceChips.map((chip) => (
                <span
                  key={chip}
                  className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium text-brand-700 bg-brand-50 border border-brand-300"
                >
                  {chip}
                </span>
              ))}

              <button
                onClick={clearVoiceSearch}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium text-clay hover:text-ink hover:bg-cream transition-colors"
              >
                <FiX className="w-3 h-3" />
                Clear
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── Mobile Filter Drawer ── */}
      {mobileFiltersOpen && (
        <div className="fixed inset-0 z-50 md:hidden" onTouchMove={(e) => e.stopPropagation()}>
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileFiltersOpen(false)} onTouchMove={(e) => e.preventDefault()} />
          <div className="absolute inset-y-0 right-0 w-full max-w-sm bg-cream shadow-xl overflow-y-auto overscroll-contain">
            <div className="flex items-center justify-between p-4 border-b border-sand bg-white sticky top-0 z-10">
              <h2 className="font-display font-normal tracking-tight text-lg text-ink">Filters</h2>
              <button onClick={() => setMobileFiltersOpen(false)} className="p-2 rounded-lg hover:bg-brand-50 transition-colors">
                <FiX className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4">
              <FilterSidebar
                key={`filter-mobile-${categoryId}`}
                onFilterChange={(f) => { handleFilterChange(f); setMobileFiltersOpen(false); }}
                categoryId={categoryId}
                initialBrand={locationState?.initialBrand}
                voiceFilters={voiceFilters}
              />
            </div>
          </div>
        </div>
      )}

      {/* ── Main Layout ── */}
      <div className="container mx-auto px-4 py-6 sm:py-8 flex-grow">
        <div className="flex flex-col md:flex-row gap-8">

          {/* Filter Sidebar — desktop */}
          <aside className="hidden md:block w-64 flex-shrink-0">
            <FilterSidebar
              key={`filter-${categoryId}`}
              onFilterChange={handleFilterChange}
              categoryId={categoryId}
              initialBrand={locationState?.initialBrand}
              voiceFilters={voiceFilters}
            />
          </aside>

          {/* Product Grid */}
          <main className="flex-grow min-w-0">
            {isLoading && (
              <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-5">
                {Array.from({ length: SKELETON_COUNT }).map((_, i) => <ProductCardSkeleton key={i} />)}
              </div>
            )}

            {!isLoading && productsShown > 0 && (
              <div className={`grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-5 transition-opacity duration-300 ${isFiltering ? 'opacity-40 pointer-events-none' : 'opacity-100'}`}>
                {products.map((product, index) => (
                  <div key={product._id} className="animate-fade-in-up" style={{ animationDelay: `${Math.min(index, 8) * 40}ms` }}>
                    <ProductCard
                      id={product._id}
                      productName={product.name}
                      currentPrice={product.priceAfterDiscount || product.price}
                      oldPrice={product.hasDiscount ? product.price : null}
                      productImageUrl={product.images?.[0]}
                      productHoverImageUrl={product.images?.[1] || product.images?.[0]}
                      category={categoryData?.name || category?.replace('-', ' ')}
                      duration={product.duration}
                      rating={product.ratingsAverage}
                      ratingsCount={product.ratingsQuantity}
                      isNew={product.subCategory === '69d0721768659c2e081b4b62'}
                    />
                  </div>
                ))}
              </div>
            )}

            {!isLoading && productsShown === 0 && (
              <div className="flex flex-col items-center justify-center py-24 text-center animate-fade-in">
                {isEmptyPageWithProducts ? (
                  <>
                    <div className="w-16 h-16 rounded-full bg-yellow-50 flex items-center justify-center mb-4">
                      <svg className="w-8 h-8 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    </div>
                    <p className="text-lg font-semibold text-ink">This page is empty</p>
                    <p className="text-clay mt-1 mb-5 text-sm">Products exist — try going back to page 1.</p>
                    <button onClick={() => { dispatch(setIsFiltering(true)); setCurrentPage(1); }} className="px-5 py-2.5 bg-ink text-cream hover:bg-brand-500 hover:text-ink rounded-xl font-semibold text-sm transition-colors">
                      Back to Page 1
                    </button>
                  </>
                ) : (
                  <>
                    <div className="w-16 h-16 rounded-full bg-brand-50 flex items-center justify-center mb-4">
                      <svg className="w-8 h-8 text-clay" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                      </svg>
                    </div>
                    <p className="text-lg font-semibold text-ink">No products found</p>
                    <p className="text-clay mt-1 text-sm">Try adjusting your filters.</p>
                    {error && <p className="text-red-400 mt-3 text-sm">{error}</p>}
                  </>
                )}
              </div>
            )}

            {!isLoading && showPagination && (
              <div className="flex justify-center mt-10">
                <Pagination
                  current={currentPage}
                  onChange={handlePageChange}
                  total={totalProductCount}
                  pageSize={21}
                  showSizeChanger={false}
                  showQuickJumper={false}
                  key={`pagination-${categoryId}`}
                  itemRender={(current, type, originalElement) => {
                    if (type === 'page' && current > maxPage) return null;
                    return originalElement;
                  }}
                />
              </div>
            )}
          </main>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default StoreProducts;
